import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Table, Badge, Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { agentService } from '../services/agentService';
import toast from 'react-hot-toast';

const MyWalletPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [wallets, setWallets] = useState({ bankCards: [], cryptoWallets: [] });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [selectedQR, setSelectedQR] = useState(null);

    // Edit Modal States
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitLoading, setSubmitLoading] = useState(false);
    const [qrPreview, setQrPreview] = useState(null);

    const fetchWallets = useCallback(async () => {
        try {
            const response = await agentService.getMyWallets();
            if (response.success) {
                setWallets({
                    bankCards: response.bankCards,
                    cryptoWallets: response.cryptoWallets
                });
            }
        } catch (error) {
            console.error('Error fetching wallets:', error);
            toast.error(t('error_history_fetch', 'Failed to load wallet addresses'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchWallets();
    }, [fetchWallets]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success(t('msg_copy_success'));
        });
    };

    const handleDelete = (item) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        try {
            if (itemToDelete.type === 'BANK') {
                await agentService.deleteMyBankCard(itemToDelete.id);
            } else {
                await agentService.deleteMyCryptoWallet(itemToDelete.id);
            }
            toast.success(t('msg_delete_success', 'Deleted successfully'));
            fetchWallets();
            setShowDeleteModal(false);
            setItemToDelete(null);
        } catch (error) {
            toast.error(t(error.message || 'error_delete_failed'));
        }
    };

    const handleStatusChange = async (item) => {
        const newStatus = item.status === 'active' ? 'inactive' : 'active';
        try {
            if (item.type === 'BANK') {
                await agentService.updateMyBankCardStatus(item.id, newStatus);
            } else {
                await agentService.updateMyCryptoWalletStatus(item.id, newStatus);
            }
            toast.success(t('msg_update_success', 'Status updated'));
            fetchWallets();
        } catch (error) {
            toast.error(t(error.message || 'error_update_failed'));
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        if (item.type === 'BANK') {
            setFormData({
                accountName: item.accountName,
                accountNumber: item.accountNumber
            });
        } else {
            setFormData({
                network: item.network,
                walletAddress: item.walletAddress,
                qrCodeImage: item.qrCodeImage || ''
            });
            setQrPreview(item.qrCodeImage || null);
        }
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            let response;
            if (editingItem.type === 'BANK') {
                response = await agentService.updateMyBankCard(editingItem.id, formData);
            } else {
                response = await agentService.updateMyCryptoWallet(editingItem.id, formData);
            }

            if (response.success) {
                toast.success(t('msg_update_success', 'Updated successfully'));
                setShowEditModal(false);
                fetchWallets();
            }
        } catch (error) {
            toast.error(t(error.message || 'error_update_failed'));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setQrPreview(reader.result);
                setFormData(prev => ({ ...prev, qrCodeImage: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="majestic-page-wrapper p-4">

            <div className="d-flex align-items-center gap-3 mb-5 fade-in">
                <div className="bg-primary bg-opacity-10 p-2 rounded-3">
                    <i className="bi bi-wallet2 text-primary fs-3"></i>
                </div>
                <h3 className="section-title mb-0">{t('add_wallet')}</h3>
            </div>

            {/* Management Cards */}
            <Row className="g-4 mb-5">
                <Col md={6} className="fade-in" style={{ animationDelay: '0.1s' }}>
                    <Card className="majestic-card h-100 text-white border-0">
                        <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="bg-primary bg-opacity-10 p-4 rounded-circle mb-4 icon-glow-blue">
                                <i className="bi bi-credit-card-2-front text-primary" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <h4 className="fw-bold mb-2">{t('bank_card')}</h4>
                            <p className="text-secondary small mb-4">{t('manage_bank_cards_desc', 'Connect and manage your bank withdrawal methods')}</p>
                            <Button className="btn-premium" onClick={() => navigate('/my-wallet/bank-card')}>
                                <i className="bi bi-grid-fill me-2"></i>
                                {t('my_bank_cards')}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} className="fade-in" style={{ animationDelay: '0.2s' }}>
                    <Card className="majestic-card h-100 text-white border-0">
                        <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="bg-warning bg-opacity-10 p-4 rounded-circle mb-4 icon-glow-warning">
                                <i className="bi bi-currency-bitcoin text-warning" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <h4 className="fw-bold mb-2">{t('crypto_wallet')}</h4>
                            <p className="text-secondary small mb-4">{t('manage_crypto_wallets_desc', 'Securely handle your cryptocurrency addresses')}</p>
                            <Button className="btn-premium-warning" onClick={() => navigate('/my-wallet/crypto-wallet')}>
                                <i className="bi bi-cpu-fill me-2"></i>
                                {t('my_crypto_wallets')}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* All Payment Addresses Section */}
            <div className="payment-header fade-in" style={{ animationDelay: '0.3s' }}>
                <h5 className="mb-0 fw-bold d-flex align-items-center">
                    <i className="bi bi-shield-check me-2 text-primary"></i>
                    {t('all_payment_addresses')}
                </h5>
            </div>

            {loading ? (
                <div className="text-center py-5 fade-in">{t('loading')}</div>
            ) : (
                <div className="d-flex flex-column gap-5 fade-in" style={{ animationDelay: '0.4s' }}>
                    {/* Bank Cards Table */}
                    <div id="bank-cards-section">
                        <div className="d-flex align-items-center gap-2 mb-4">
                            <i className="bi bi-bank2 text-primary fs-5"></i>
                            <h6 className="mb-0 fw-bold">{t('bank_accounts')}</h6>
                            <Badge bg="primary" className="bg-opacity-10 text-primary border border-primary border-opacity-25 px-2">
                                {wallets.bankCards.length}
                            </Badge>
                        </div>
                        {wallets.bankCards.length === 0 ? (
                            <div className="glass-card p-4 text-center text-muted small">{t('no_bank_accounts')}</div>
                        ) : (
                            <UniversalTable
                                data={wallets.bankCards.map(c => ({
                                    ...c,
                                    type: 'BANK',
                                    status: c.status || 'active'
                                }))}
                                copyToClipboard={copyToClipboard}
                                onDelete={handleDelete}
                                onStatusChange={handleStatusChange}
                                onQRClick={setSelectedQR}
                                onEdit={handleEdit}
                            />
                        )}
                    </div>

                    {/* Crypto Wallets Table */}
                    <div id="crypto-wallets-section">
                        <div className="d-flex align-items-center gap-2 mb-4">
                            <i className="bi bi-lightning-charge-fill text-warning fs-5"></i>
                            <h6 className="mb-0 fw-bold">{t('crypto_wallets_title')}</h6>
                            <Badge bg="warning" className="bg-opacity-10 text-warning border border-warning border-opacity-25 px-2">
                                {wallets.cryptoWallets.length}
                            </Badge>
                        </div>
                        {wallets.cryptoWallets.length === 0 ? (
                            <div className="glass-card p-4 text-center text-muted small">{t('no_crypto_wallets_added')}</div>
                        ) : (
                            <UniversalTable
                                data={wallets.cryptoWallets.map(w => ({
                                    ...w,
                                    type: 'CRYPTO',
                                    status: w.status || 'active'
                                }))}
                                copyToClipboard={copyToClipboard}
                                onDelete={handleDelete}
                                onStatusChange={handleStatusChange}
                                onQRClick={setSelectedQR}
                                onEdit={handleEdit}
                            />
                        )}
                    </div>
                </div>
            )}


            {/* Majestic Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
                contentClassName="majestic-card border-0 shadow-lg"
                transition={false}
            >
                <div className="majestic-modal-content p-0" style={{ background: '#0d1117', borderRadius: '20px', overflow: 'hidden' }}>
                    <div
                        className="p-5 text-center position-relative"
                        style={{
                            background: 'linear-gradient(180deg, rgba(220, 53, 69, 0.1) 0%, transparent 100%)',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                        }}
                    >
                        <div className="bg-danger bg-opacity-10 p-4 rounded-circle d-inline-block mb-4 shadow-sm" style={{ border: '1px solid rgba(220, 53, 69, 0.2)' }}>
                            <i className="bi bi-trash3-fill text-danger" style={{ fontSize: '2.5rem' }}></i>
                        </div>
                        <h4 className="text-white fw-bold mb-3">{t('confirm_delete_title', 'Delete Confirmation')}</h4>
                        <p className="text-secondary px-3 mb-0 lh-lg" style={{ fontSize: '0.95rem' }}>
                            {t('confirm_delete_extra', 'This will permanently remove contact from your account')}
                        </p>
                    </div>

                    <div className="p-4 d-flex align-items-center justify-content-center gap-3 bg-dark bg-opacity-50">
                        <Button
                            variant="link"
                            className="text-secondary text-decoration-none fw-bold px-4 hover-text-white transition-all"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="danger"
                            className="rounded-pill px-5 py-2 fw-bold shadow-lg"
                            style={{
                                background: 'linear-gradient(135deg, #dc3545 0%, #ff4d5a 100%)',
                                border: 'none',
                                boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)'
                            }}
                            onClick={confirmDelete}
                        >
                            {t('confirm_delete_action', 'Permanently Delete')}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered contentClassName="majestic-card border-0 shadow-lg">
                <Modal.Header closeButton className="border-bottom border-light border-opacity-10">
                    <Modal.Title className="text-white fw-bold">{editingItem?.type === 'BANK' ? t('edit_bank_card') : t('edit_crypto_wallet')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body className="p-4">
                        {editingItem?.type === 'BANK' ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-secondary small text-uppercase fw-bold">{t('cardholder_name')}</Form.Label>
                                    <Form.Control
                                        className="majestic-input-field"
                                        value={formData.accountName || ''}
                                        onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-secondary small text-uppercase fw-bold">{t('card_number')}</Form.Label>
                                    <Form.Control
                                        className="majestic-input-field"
                                        value={formData.accountNumber || ''}
                                        onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-secondary small text-uppercase fw-bold">{t('network')}</Form.Label>
                                    <Form.Control
                                        className="majestic-input-field"
                                        value={formData.network || ''}
                                        onChange={(e) => setFormData({ ...formData, network: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-secondary small text-uppercase fw-bold">{t('wallet_address')}</Form.Label>
                                    <Form.Control
                                        className="majestic-input-field"
                                        value={formData.walletAddress || ''}
                                        onChange={(e) => setFormData({ ...formData, walletAddress: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-secondary small text-uppercase fw-bold">{t('wallet_qr_code')}</Form.Label>
                                    <div className="d-flex align-items-center gap-3">
                                        {qrPreview && <img src={qrPreview} alt="QR Preview" style={{ width: '60px', height: '60px', objectFit: 'contain' }} />}
                                        <Form.Control type="file" accept="image/*" onChange={handleImageUpload} />
                                    </div>
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-top border-light border-opacity-10">
                        <Button variant="link" className="text-secondary text-decoration-none" onClick={() => setShowEditModal(false)}>{t('cancel')}</Button>
                        <Button variant="primary" type="submit" disabled={submitLoading} className="rounded-pill px-4">
                            {submitLoading ? t('saving') : t('save')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* QR Large View Modal */}
            {selectedQR && (
                <div
                    className="qr-modal-overlay"
                    onClick={() => setSelectedQR(null)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}
                >
                    <div
                        className="qr-modal-content"
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'linear-gradient(145deg, #1a1a1a, #0d0d0d)',
                            padding: '25px',
                            borderRadius: '24px',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            width: '320px',
                            textAlign: 'center'
                        }}
                    >
                        <div className="mb-3">
                            <i className="bi bi-qr-code text-warning fs-3"></i>
                            <h5 className="text-white mt-2 fw-bold">{t('wallet_qr_code')}</h5>
                        </div>
                        <img src={selectedQR} alt="QR" style={{ width: '100%', borderRadius: '16px', background: '#fff', padding: '10px' }} />
                        <Button variant="warning" className="w-100 mt-4 rounded-pill fw-bold" onClick={() => setSelectedQR(null)}>{t('close')}</Button>
                    </div>
                </div>
            )}

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .qr-modal-content {
                    animation: fadeIn 0.3s ease;
                }
                `}
            </style>
        </div>
    );
};

// Generic Table with Pagination
const UniversalTable = ({ data, copyToClipboard, onDelete, onStatusChange, onQRClick, onEdit }) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const totalPages = Math.ceil(data.length / itemsPerPage);
    const currentItems = data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const isBank = data[0]?.type === 'BANK';

    return (
        <>
            <style>
                {`
                .premium-table-card {
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 16px;
                    overflow: visible;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
                }

                .premium-table-card .table-responsive {
                    overflow-x: auto !important;
                    -webkit-overflow-scrolling: touch;
                }

                .premium-table thead th {
                    background: rgba(64, 158, 255, 0.05) !important;
                    color: white !important;
                    text-transform: uppercase;
                    font-size: 0.7rem;
                    letter-spacing: 1px;
                    padding: 15px 20px;
                    border-bottom: 2px solid rgba(64, 158, 255, 0.2) !important;
                }

                .premium-table tbody td {
                    background: transparent !important;
                    color: var(--text-regular) !important;
                    padding: 18px 20px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
                    transition: all 0.2s ease;
                }

                .premium-table tbody tr:hover td {
                    background: rgba(255, 255, 255, 0.03) !important;
                    color: #fff !important;
                }

                .copy-badge {
                    background: rgba(64, 158, 255, 0.1);
                    color: var(--primary-blue);
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-size: 10px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .status-badge {
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border-radius: 50px;
                    padding: 4px 12px;
                    font-size: 11px;
                    text-transform: capitalize;
                    border: 1px solid transparent;
                    display: inline-block;
                    width: 70px;
                    text-align: center;
                }

                .status-badge.active {
                    background: linear-gradient(135deg, rgba(103, 194, 58, 0.2) 0%, rgba(103, 194, 58, 0.1) 100%);
                    color: #67C23A;
                    border-color: rgba(103, 194, 58, 0.3);
                }

                .status-badge.inactive {
                    background: linear-gradient(135deg, rgba(245, 108, 108, 0.2) 0%, rgba(245, 108, 108, 0.1) 100%);
                    color: #F56C6C;
                    border-color: rgba(245, 108, 108, 0.3);
                }

                .action-btn {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    border: none;
                    transition: all 0.2s ease;
                    background: rgba(255, 255, 255, 0.05);
                }

                .edit-btn { color: #E6A23C; background: rgba(230, 162, 60, 0.1); }
                .edit-btn:hover { background: #E6A23C; color: white; }

                .delete-btn { color: #F56C6C; background: rgba(245, 108, 108, 0.1); }
                .delete-btn:hover { background: #F56C6C; color: white; transform: rotate(15deg); }
                `}
            </style>
            <div className="premium-table-card mb-4">
                <div className="table-responsive">
                    <Table hover className="premium-table mb-0 align-middle">
                        <thead>
                            <tr>
                                {isBank ? (
                                    <>
                                        <th style={{ width: '35%' }}>{t('cardholder_name', 'Cardholder Name')}</th>
                                        <th style={{ width: '45%' }}>{t('card_number', 'Card Number')}</th>
                                    </>
                                ) : (
                                    <>
                                        <th style={{ width: '160px' }}>{t('network')}</th>
                                        <th style={{ paddingLeft: '40px', width: '45%' }}>{t('wallet_address')}</th>
                                        <th style={{ width: '180px', textAlign: 'center' }}>{t('wallet_qr_code', 'QR')}</th>
                                    </>
                                )}
                                <th style={{ width: '130px', textAlign: 'end' }}>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(item => (
                                <tr key={item.id}>
                                    {isBank ? (
                                        <>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="bg-primary bg-opacity-10 p-2 rounded-circle border border-primary border-opacity-10">
                                                        <i className="bi bi-person-fill text-primary small"></i>
                                                    </div>
                                                    <span className="fw-bold">{item.accountName}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="font-monospace text-secondary" style={{ fontSize: '0.9rem' }}>{item.accountNumber}</span>
                                                    <span className="copy-badge" onClick={() => copyToClipboard(item.accountNumber)}>
                                                        <i className="bi bi-files me-1"></i>
                                                        COPY
                                                    </span>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="bg-warning bg-opacity-10 p-2 rounded-circle border border-warning border-opacity-10">
                                                        <i className="bi bi-currency-exchange text-warning small"></i>
                                                    </div>
                                                    <span className="fw-bold text-warning">{item.network}</span>
                                                </div>
                                            </td>
                                            <td style={{ paddingLeft: '40px' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="font-monospace text-secondary text-break" style={{ fontSize: '0.9rem' }}>{item.walletAddress}</span>
                                                    <span className="copy-badge" onClick={() => copyToClipboard(item.walletAddress)}>
                                                        <i className="bi bi-files me-1"></i>
                                                        COPY
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                {item.qrCodeImage ? (
                                                    <img
                                                        src={item.qrCodeImage}
                                                        alt="QR"
                                                        style={{ width: '45px', height: '45px', objectFit: 'contain', background: '#fff', borderRadius: '4px', padding: '2px', cursor: 'pointer' }}
                                                        onClick={() => onQRClick && onQRClick(item.qrCodeImage)}
                                                    />
                                                ) : (
                                                    <span className="text-secondary opacity-50">-</span>
                                                )}
                                            </td>
                                        </>
                                    )}
                                    <td className="text-end">
                                        <div className="d-flex align-items-center justify-content-end gap-2">
                                            <span
                                                className={`status-badge ${item.status === 'active' ? 'active' : 'inactive'}`}
                                                onClick={() => onStatusChange && onStatusChange(item)}
                                                title={t('click_to_toggle_status')}
                                            >
                                                {item.status || 'Active'}
                                            </span>

                                            <button className="action-btn edit-btn" onClick={() => onEdit && onEdit(item)} title={t('edit')}>
                                                <i className="bi bi-pencil-square"></i>
                                            </button>

                                            <button className="action-btn delete-btn" onClick={() => onDelete && onDelete(item)} title={t('delete')}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mb-4 pagination-glow">
                    <div className="d-flex align-items-center gap-2 p-2 glass-card rounded-pill">
                        <Button variant="light" size="sm" className="rounded-circle" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>
                            <i className="bi bi-chevron-left"></i>
                        </Button>
                        {[...Array(totalPages)].map((_, i) => (
                            <Button
                                key={i + 1}
                                variant={currentPage === i + 1 ? 'primary' : 'light'}
                                size="sm"
                                className="rounded-circle"
                                style={{ width: '32px', height: '32px', padding: 0 }}
                                onClick={() => setCurrentPage(i + 1)}
                            >
                                {i + 1}
                            </Button>
                        ))}
                        <Button variant="light" size="sm" className="rounded-circle" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>
                            <i className="bi bi-chevron-right"></i>
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default MyWalletPage;
